'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { Filter } from '@terra/ui-design-system';
import {
  EXTERNAL_NETWORK_SIGNAL_ORIGIN_FILTER_VALUES,
  getOriginFilterOptions,
  getSourceFilterOptions,
} from '@terra/shared-contracts-signals';
import { useTargetType } from '../../../store/projectStore';
import { ConfidenceLevelSchema } from '../../../types/confidence';
import { useUsers } from '../../../hooks/useUsers';
import {
  ListPageAdvancedFilterField,
  ListPageAdvancedFilters,
} from '../../common/ListPageAdvancedFilters';
import { ListPageDateRangeFilter } from '../../common/ListPageDateRangeFilter';
import { pickFilterOptions } from '../../common/list-page-filter-options';
import type { SignalsListFilters } from '../signals-table/useSignalsListFilters';
import { formatSignalConfidenceLabel } from '../signals-table/signals-list.model';
import {
  SIGNAL_PRESENCE_FILTER_VALUES,
  toSignalPresenceFilter,
  toSignalPresenceFilterValue,
} from './signals-filter-options';

const UNASSIGNED_VALUE = 'UNASSIGNED';
const CONFIDENCE_ITEMS = ConfidenceLevelSchema.options.map((level) => ({
  value: level,
  label: formatSignalConfidenceLabel(level) ?? level,
}));

export function SignalsAdvancedFilters({
  filters,
}: {
  filters: SignalsListFilters;
}) {
  const t = useTranslations('proxy.signals.filters');
  const tSignalsFilters = useTranslations('signals.filters');
  const targetType = useTargetType();
  const { data: users } = useUsers();
  const presenceItems = useMemo(() => {
    const toItems = (
      filterKey: 'hasExecution' | 'hasFinding' | 'hasSecurityTestCase',
    ) =>
      SIGNAL_PRESENCE_FILTER_VALUES.map((value) => ({
        value,
        label: t(`${filterKey}.${value}`),
      }));

    return {
      execution: toItems('hasExecution'),
      finding: toItems('hasFinding'),
      securityTest: toItems('hasSecurityTestCase'),
    };
  }, [t]);
  const originItems = useMemo(
    () =>
      getOriginFilterOptions((key) => tSignalsFilters(key as never), {
        origins:
          targetType === 'EXTERNAL_NETWORK'
            ? EXTERNAL_NETWORK_SIGNAL_ORIGIN_FILTER_VALUES
            : undefined,
      }),
    [targetType, tSignalsFilters],
  );
  const sourceItems = useMemo(
    () =>
      getSourceFilterOptions((key) => tSignalsFilters(key as never), {
        origins: filters.originFilter,
      }),
    [filters.originFilter, tSignalsFilters],
  );
  const assigneeItems = useMemo(
    () => [
      { value: UNASSIGNED_VALUE, label: t('unassigned') },
      ...(users ?? []).map((user) => ({ value: user.id, label: user.name })),
    ],
    [t, users],
  );

  return (
    <ListPageAdvancedFilters>
      <ListPageAdvancedFilterField>
        <Filter
          id="signal-confidence"
          density="compact"
          isMultiSelect
          placeholder={t('confidence')}
          items={CONFIDENCE_ITEMS}
          value={filters.confidenceFilter}
          onChange={(values) =>
            filters.setConfidenceFilter(
              pickFilterOptions({
                options: CONFIDENCE_ITEMS.map((item) => item.value),
                values,
              }),
            )
          }
        />
      </ListPageAdvancedFilterField>
      <ListPageAdvancedFilterField>
        <Filter
          id="signal-origin"
          density="compact"
          isMultiSelect
          placeholder={tSignalsFilters('origin.label')}
          items={originItems}
          value={filters.originFilter}
          onChange={(values) =>
            filters.setOriginFilter(
              pickFilterOptions({
                options: originItems.map((item) => item.value),
                values,
              }),
            )
          }
        />
      </ListPageAdvancedFilterField>
      {filters.showThirdPartySource ? (
        <ListPageAdvancedFilterField>
          <Filter
            id="signal-source"
            density="compact"
            isMultiSelect
            placeholder={tSignalsFilters('source.label')}
            items={sourceItems}
            value={filters.sourceFilter}
            onChange={(values) =>
              filters.setSourceFilter(
                pickFilterOptions({
                  options: sourceItems.map((item) => item.value),
                  values,
                }),
              )
            }
          />
        </ListPageAdvancedFilterField>
      ) : null}
      <ListPageAdvancedFilterField>
        <Filter
          id="signal-assignee"
          density="compact"
          isMultiSelect
          placeholder={t('assignee')}
          items={assigneeItems}
          value={filters.assigneeFilter}
          onChange={filters.setAssigneeFilter}
        />
      </ListPageAdvancedFilterField>
      <ListPageAdvancedFilterField>
        <Filter
          id="signal-has-execution"
          density="compact"
          placeholder={t('hasExecution.label')}
          items={presenceItems.execution}
          value={toSignalPresenceFilterValue(filters.hasExecutionFilter)}
          onChange={([value]) =>
            filters.setHasExecutionFilter(toSignalPresenceFilter(value))
          }
        />
      </ListPageAdvancedFilterField>
      <ListPageAdvancedFilterField>
        <Filter
          id="signal-has-finding"
          density="compact"
          placeholder={t('hasFinding.label')}
          items={presenceItems.finding}
          value={toSignalPresenceFilterValue(filters.hasFindingFilter)}
          onChange={([value]) =>
            filters.setHasFindingFilter(toSignalPresenceFilter(value))
          }
        />
      </ListPageAdvancedFilterField>
      {filters.isNetworkTarget ? null : (
        <ListPageAdvancedFilterField>
          <Filter
            id="signal-has-security-test-case"
            density="compact"
            placeholder={t('hasSecurityTestCase.label')}
            items={presenceItems.securityTest}
            value={toSignalPresenceFilterValue(filters.securityTestFilter)}
            onChange={([value]) =>
              filters.setSecurityTestFilter(toSignalPresenceFilter(value))
            }
          />
        </ListPageAdvancedFilterField>
      )}
      <ListPageAdvancedFilterField>
        <ListPageDateRangeFilter
          label={t('testedAt')}
          startDate={filters.testedStartDate}
          endDate={filters.testedEndDate}
          onStartDateChange={filters.setTestedStartDate}
          onEndDateChange={filters.setTestedEndDate}
        />
      </ListPageAdvancedFilterField>
    </ListPageAdvancedFilters>
  );
}
